import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";

const DISCLAIMER =
  "These terms govern your use of the Regulens platform. Regulens is not a law firm and these terms do not constitute legal advice.";

const SUPPORT_EMAIL = "support@getregulens.com";

const Section = ({ title, children, testId }) => (
  <section data-testid={testId}>
    <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">{title}</h2>
    {children}
  </section>
);

export default function TermsOfService() {
  return (
    <Layout>
      <section
        className="max-w-3xl mx-auto px-6 py-12"
        data-testid="terms-of-service-page"
      >
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">
          Legal
        </div>
        <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-4">
          Terms of Service
        </h1>

        <div
          className="mb-8 rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-4 text-sm text-[var(--rg-amber)]"
          data-testid="terms-disclaimer"
        >
          {DISCLAIMER}
        </div>

        <article className="prose-invert space-y-6 text-[var(--rg-text)] leading-relaxed">
          <Section title="1. What Regulens provides" testId="terms-section-provides">
            <ul className="list-disc pl-6 space-y-1 text-sm">
              <li>
                Regulens provides <strong>informational readiness scores and
                reports</strong> based on publicly available U.S. government
                reference points across all covered jurisdictions.
              </li>
              <li>
                Readiness scores are <strong>not legal advice, not tax advice,
                not professional advisory, and not a compliance certification.</strong>
              </li>
              <li>
                Use of the platform does not create an attorney–client,
                accountant–client, or any other professional relationship.
              </li>
              <li>
                Always consult a licensed professional before making
                compliance, tax, or legal decisions for your business.
              </li>
            </ul>
          </Section>

          <Section title="2. Scores do not constitute legal compliance certification" testId="terms-section-no-cert">
            <p className="text-sm">
              A high readiness score does <strong>not</strong> mean your store
              is legally compliant. A low readiness score does <strong>not
              </strong> mean your store is non-compliant. Scores are
              educational signals derived from self-reported merchant data and
              public statute references — they are not audits, opinions, or
              attestations under any state or federal regulatory framework.
            </p>
          </Section>

          <Section title="3. User responsibilities and acceptable use" testId="terms-section-acceptable">
            <ul className="list-disc pl-6 space-y-1 text-sm">
              <li>Provide truthful, accurate inputs (revenue, transactions, jurisdictions, policies).</li>
              <li>Use the platform only for your own business or for clients you are authorized to represent.</li>
              <li>Do not share account credentials. Each subscription seat is single-user unless explicitly stated.</li>
              <li>Do not scrape, reverse-engineer, or attempt to extract source data or compiled models.</li>
              <li>Do not use Regulens for any unlawful purpose, or to mislead third parties about your compliance posture.</li>
              <li>Do not resell, republish, or rebrand reports as your own service without an active Agency-tier license.</li>
            </ul>
          </Section>

          <Section title="4. Payment terms" testId="terms-section-payment">
            <ul className="list-disc pl-6 space-y-1 text-sm">
              <li>
                <strong>All transactions are processed in USD.</strong> Regulens
                is not responsible for currency-conversion rates, foreign-
                transaction fees, or international processing fees applied by
                your bank, card issuer, or financial institution.
              </li>
              <li>
                Subscriptions renew automatically until cancelled. Cancellation
                takes effect at the end of the current billing period.
              </li>
              <li>
                Failed payments may result in immediate downgrade to the Free
                tier and loss of access to paid features.
              </li>
              <li>
                Refunds are governed by the{" "}
                <Link to="/refund-policy" className="text-[var(--rg-sage)] hover:underline" data-testid="terms-link-refund">
                  Refund Policy
                </Link>
                .
              </li>
            </ul>
          </Section>

          <Section title="5. No-chargeback abuse policy" testId="terms-section-chargeback">
            <p className="text-sm">
              Initiating a chargeback or payment dispute with your card issuer
              <strong> without first contacting</strong>{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[var(--rg-sage)] hover:underline"
                data-testid="terms-contact-email"
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              and exhausting the refund-request process in our{" "}
              <Link to="/refund-policy" className="text-[var(--rg-sage)] hover:underline">
                Refund Policy
              </Link>{" "}
              is considered <strong>chargeback abuse</strong>. Accounts that
              initiate fraudulent or unjustified chargebacks may be:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-sm mt-2">
              <li>Immediately and permanently suspended.</li>
              <li>Charged the original amount plus any chargeback fees incurred by Regulens.</li>
              <li>Reported to fraud-prevention databases and our payment processor.</li>
              <li>Pursued through small-claims court for recovery of fees and losses.</li>
            </ul>
          </Section>

          <Section title="6. Limitation of liability" testId="terms-section-liability">
            <p className="text-sm">
              To the maximum extent permitted by law, Regulens, its officers,
              employees, contractors, and affiliates shall not be liable for
              any indirect, incidental, consequential, special, exemplary, or
              punitive damages arising from your use of the platform.
              Regulens&apos; total cumulative liability to any user is{" "}
              <strong>limited to the amount that user paid Regulens in the
              preceding twelve (12) months</strong>, or USD $100, whichever is
              lower.
            </p>
          </Section>

          <Section title="7. No warranty" testId="terms-section-warranty">
            <p className="text-sm">
              The service is provided &quot;as is&quot; and &quot;as available&quot; without any
              warranty of any kind, express or implied, including but not
              limited to warranties of merchantability, fitness for a
              particular purpose, accuracy, or non-infringement. Laws and
              regulations change frequently &mdash; Regulens does not warrant that
              the data shown is current at the moment of viewing.
            </p>
          </Section>

          <Section title="8. Account termination" testId="terms-section-termination">
            <p className="text-sm">
              Regulens may suspend or terminate accounts that violate these
              terms, including for non-payment, fraudulent activity, abusive
              chargebacks, attempted security breaches, or material
              misrepresentation. Terminated accounts forfeit access without
              refund, except where required by the{" "}
              <Link to="/refund-policy" className="text-[var(--rg-sage)] hover:underline">
                Refund Policy
              </Link>
              .
            </p>
          </Section>

          <Section title="9. Changes to these terms" testId="terms-section-changes">
            <p className="text-sm">
              Regulens may update these terms from time to time. Material
              changes will be announced in-app or via email to the account
              holder. Continued use of the platform after the effective date
              constitutes acceptance of the updated terms.
            </p>
          </Section>

          <Section title="10. Contact" testId="terms-section-contact">
            <p className="text-sm">
              Questions about these terms can be sent to{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[var(--rg-sage)] hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <p className="text-xs text-[var(--rg-muted)] italic mt-8">
            These terms were last updated June 2026.
          </p>
        </article>
      </section>
    </Layout>
  );
}
